/**
 * Budgets vs Actuals — Wave F.
 *
 * Per-account, per-month budget cells (`accountId × yyyy-mm → amount`).
 * Variance is derived live from posted JEs — never stored, never drifts.
 *
 * Read pattern (UI):
 *   const rows = getBudgetVariance(period, budgets, accounts, journalEntries);
 *   // → [{ accountId, accountName, budget, actual, variance, variancePct, status }]
 *
 * `status` rule (consistent with Close Readiness language):
 *   on_track | watch (>10% over for expenses, >10% under for revenue)
 *   | breach (>25%)
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
} from "react";
import type { GLAccount, JournalEntry } from "../glTypes";

export interface BudgetCell {
  accountId: string;
  period: string;   // yyyy-mm
  amount: number;
  note?: string;
  updatedAt: string;
  updatedBy: string;
}

interface BudgetsContextValue {
  budgets: BudgetCell[];
  setBudget: (accountId: string, period: string, amount: number, note?: string) => void;
  bulkSetForPeriod: (period: string, cells: Array<{ accountId: string; amount: number }>) => void;
  clear: (accountId: string, period: string) => void;
  getBudget: (accountId: string, period: string) => number;
}

const Ctx = createContext<BudgetsContextValue | null>(null);

export function BudgetsProvider({ children }: { children: React.ReactNode }) {
  const [budgets, setBudgets] = useState<BudgetCell[]>([]);

  const setBudget = useCallback((accountId: string, period: string, amount: number, note?: string) => {
    if (amount < 0) throw new Error("Budget amount must be >= 0.");
    setBudgets(prev => {
      const others = prev.filter(b => !(b.accountId === accountId && b.period === period));
      return [...others, {
        accountId, period, amount, note,
        updatedAt: new Date().toISOString(), updatedBy: "Steve",
      }];
    });
  }, []);

  const bulkSetForPeriod = useCallback((period: string, cells: Array<{ accountId: string; amount: number }>) => {
    setBudgets(prev => {
      const others = prev.filter(b => b.period !== period);
      const next = cells
        .filter(c => c.amount > 0)
        .map(c => ({
          accountId: c.accountId, period, amount: c.amount,
          updatedAt: new Date().toISOString(), updatedBy: "Steve",
        }));
      return [...others, ...next];
    });
  }, []);

  const clear = useCallback((accountId: string, period: string) => {
    setBudgets(prev => prev.filter(b => !(b.accountId === accountId && b.period === period)));
  }, []);

  const getBudget = useCallback((accountId: string, period: string) => {
    return budgets.find(b => b.accountId === accountId && b.period === period)?.amount ?? 0;
  }, [budgets]);

  const value = useMemo(() => ({ budgets, setBudget, bulkSetForPeriod, clear, getBudget }),
    [budgets, setBudget, bulkSetForPeriod, clear, getBudget]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBudgets() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useBudgets must be used inside <BudgetsProvider>");
  return ctx;
}

// ───────────────── pure variance selector ─────────────────

export type VarianceStatus = "on_track" | "watch" | "breach";

export interface BudgetVarianceRow {
  accountId: string;
  accountNumber: string;
  accountName: string;
  type: GLAccount["type"];
  budget: number;
  actual: number;
  variance: number;          // actual - budget (negative = favorable for expense)
  variancePct: number;       // 0..1 (or >1)
  status: VarianceStatus;
  direction: "favorable" | "unfavorable" | "neutral";
}

/** Actuals for a single account during a single yyyy-mm period. */
function actualForAccount(
  account: GLAccount,
  period: string,
  journalEntries: JournalEntry[],
): number {
  let debits = 0, credits = 0;
  for (const je of journalEntries) {
    if (je.status !== "posted") continue;
    if (je.postingDate.slice(0, 7) !== period) continue;
    for (const l of je.lines) {
      if (l.accountId !== account.id) continue;
      if (l.side === "debit") debits += l.amount; else credits += l.amount;
    }
  }
  // Normal-balance net: expense/asset/debit-normal → DR - CR; revenue/liability/credit-normal → CR - DR.
  return account.normalBalance === "debit" ? debits - credits : credits - debits;
}

export function getBudgetVariance(
  period: string,
  budgets: BudgetCell[],
  accounts: GLAccount[],
  journalEntries: JournalEntry[],
): BudgetVarianceRow[] {
  // Universe = every account with a budget for the period OR any actual posted.
  const cellsThisPeriod = budgets.filter(b => b.period === period);
  const budgetedIds = new Set(cellsThisPeriod.map(c => c.accountId));
  const accountsToReport = accounts.filter(a =>
    (a.type === "revenue" || a.type === "expense") &&
    a.active &&
    (budgetedIds.has(a.id) || actualForAccount(a, period, journalEntries) !== 0)
  );

  const rows: BudgetVarianceRow[] = accountsToReport.map(a => {
    const budget = cellsThisPeriod.find(c => c.accountId === a.id)?.amount ?? 0;
    const actual = Math.round(actualForAccount(a, period, journalEntries) * 100) / 100;
    const variance = Math.round((actual - budget) * 100) / 100;
    const variancePct = budget > 0 ? Math.abs(variance) / budget : (actual === 0 ? 0 : 1);

    // For expenses: over-budget = unfavorable. For revenue: under-budget = unfavorable.
    let direction: BudgetVarianceRow["direction"];
    if (budget === 0 && actual === 0) direction = "neutral";
    else if (a.type === "expense") direction = actual > budget ? "unfavorable" : actual < budget ? "favorable" : "neutral";
    else direction = actual < budget ? "unfavorable" : actual > budget ? "favorable" : "neutral";

    let status: VarianceStatus = "on_track";
    if (direction === "unfavorable") {
      if (variancePct > 0.25) status = "breach";
      else if (variancePct > 0.10) status = "watch";
    }

    return {
      accountId: a.id,
      accountNumber: a.number,
      accountName: a.name,
      type: a.type,
      budget,
      actual,
      variance,
      variancePct,
      status,
      direction,
    };
  });

  // Sort: breaches first, then by absolute variance dollars.
  const order: Record<VarianceStatus, number> = { breach: 0, watch: 1, on_track: 2 };
  return rows.sort((a, b) =>
    order[a.status] - order[b.status] ||
    Math.abs(b.variance) - Math.abs(a.variance) ||
    a.accountNumber.localeCompare(b.accountNumber)
  );
}
